import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { DATA_SOURCE, environment } from 'src/environments/environment';
import { QueryParams } from '../model/interfaces/request';
import { HttpControllerArg, HttpControllerArgFetch } from '../model/interfaces/http-controller';
import { ApiResponse } from '../model/interfaces/response';

export abstract class HttpController<Q> {
  protected _queryParams: QueryParams<Q>

  constructor(
    private readonly _HttpClient: HttpClient,
    protected _endpoint: string
  ) {}

  public get queryParams(): QueryParams<Q> {
    return this._queryParams;
  }
  public set queryParams(queryParams: QueryParams<Q>) {
    this._queryParams = queryParams;
  }

  /**
   * The function handles different types of errors that can occur during an HTTP request and returns the
   * error as an observable.
   * @param {HttpErrorResponse} httpErrorResponse - The parameter `httpErrorResponse` is an object of
   * type `HttpErrorResponse` which represents an HTTP response error. It contains information about the
   * error that occurred during an HTTP request, such as the error status code, error message, and the
   * response body.
   * @returns an Observable that emits the HttpErrorResponse object.
  */
  private errorHandler(httpErrorResponse: HttpErrorResponse) {
    //? A client-side or network error occurred. Handle it accordingly.
    if (httpErrorResponse.error instanceof ArrayBuffer) console.error('An error [ArrayBuffer] occurred:', httpErrorResponse.error);
    //? A client-side or network error occurred. Handle it accordingly.
    if (httpErrorResponse.error instanceof ErrorEvent) console.error('An error [ErrorEvent] occurred:', httpErrorResponse.error.message);
    //? The backend returned an unsuccessful response code.
    //? The response body may contain clues as to what went wrong,
    // console.error(`Backend returned [ErrorEvent] code ${httpErrorResponse.status},body was: ${JSON.stringify(httpErrorResponse.error)}`);
    return throwError(httpErrorResponse);
  }

  /**
   * The `read` function sends an HTTP GET request to a specified apiPath and returns an Observable of
   * the response, wrapped in an ApiResponse object.
   * @param httpControllerArgs - An object that contains the arguments for making an HTTP request. It has
   * the following properties:
   * @returns an Observable of type ApiResponse<K>.
  */
  public read<K>(httpControllerArgs: HttpControllerArgFetch<Q>): Observable<ApiResponse<K>> {
    const params = new HttpParams({ fromObject: { ...httpControllerArgs.queryParams } });
    return this._HttpClient
      .get<ApiResponse<K>>(`${this.buildUrl(httpControllerArgs.datasource, httpControllerArgs.path)}`,
        {
          headers: this.headers,
          params: httpControllerArgs.queryParams ? params : undefined,
        }
      )
      .pipe(catchError(this.errorHandler))
  }

  /**
   * The `create` function sends a POST request to a specified apiPath with the provided body and query
   * parameters, and returns an Observable of the API response.
   * @param httpControllerArgs - The `httpControllerArgs` parameter is an object that contains the
   * following properties:
   * @returns an Observable of type ApiResponse<K>.
  */
  public create<T, R>(httpControllerArgs: HttpControllerArg<T>): Observable<ApiResponse<R>> {
    return this._HttpClient
      .post<ApiResponse<R>>(`${this.buildUrl(httpControllerArgs.datasource, httpControllerArgs.path)}`, httpControllerArgs.body,
        {
          headers: this.headers
        }
      )
      .pipe(
        catchError(this.errorHandler),
      );
  }

  public createMultipart<R>(httpControllerArgs: HttpControllerArg<void>, body: FormData): Observable<ApiResponse<R>> {
    return this._HttpClient
      .post<ApiResponse<R>>(`${this.buildUrl(httpControllerArgs.datasource, httpControllerArgs.path)}`, body,
        {
          headers: new HttpHeaders({
            'Authorization': `${localStorage.getItem('accessToken')}`
          })
        }
      )
      .pipe(
        catchError(this.errorHandler),
      );
  }

  /**
   * The function `update` sends a PUT request to a specified apiPath with the provided arguments and
   * returns an Observable of the API response.
   * @param httpControllerArgs - The `httpControllerArgs` parameter is an object that contains the
   * following properties:
   * @returns an Observable of type ApiResponse<K>.
  */
  public update<T, R>(httpControllerArgs: HttpControllerArg<T>): Observable<ApiResponse<R>> {
    return this._HttpClient
      .put<ApiResponse<R>>(`${this.buildUrl(httpControllerArgs.datasource, httpControllerArgs.path)}`, httpControllerArgs.body,
        {
          headers: this.headers
        }
      )
      .pipe(
        catchError(this.errorHandler),
      );
  }

  /**
   * The `delete` function sends a DELETE request to a specified apiPath with optional query parameters
   * and a request body, and returns an Observable that emits an ApiResponse.
   * @param httpControllerArgs - The `httpControllerArgs` parameter is an object that contains the
   * following properties:
   * @returns an Observable of type ApiResponse<K>.
  */
  public delete<R>(httpControllerArgs: HttpControllerArg<Q>): Observable<ApiResponse<R>> {
    return this._HttpClient
      .delete<ApiResponse<R>>(`${this.buildUrl(httpControllerArgs.datasource, httpControllerArgs.path)}`,
        {
          headers: this.headers,
          body: httpControllerArgs.body
        }
      )
      .pipe(catchError(this.errorHandler))
  }

  /**
   * The function returns the apiPath URL based on the values provided in the environment object.
   * @returns The `apiPath` being returned is a string that concatenates various environment variables
   * to form a URL.
  */
  private apiPath(DATA_SOURCE:DATA_SOURCE): string {
    const url =
      `${environment['protocol']}://` +
      `${environment['subDomain'] ? environment['subDomain'] + '.' : ''}` +
      `${environment['domain']}` +
      `${environment['port'] ? `:${environment['port']}` : ''}/` +
      `${environment['gateway'] ? environment['gateway'] + '/' : ''}` +
      `${DATA_SOURCE}/` +
      `${environment['apiVersion']}/` +
      `${environment['prefix']}/` +
      `${environment['service']}`
    return url.trim()
  }

  /**
   * The function returns a HttpHeaders object with the 'Authorization' and 'Content-Type' headers set.
   * @returns The method is returning an instance of the HttpHeaders class.
  */
  private get headers(): HttpHeaders {
    return new HttpHeaders({
      'Authorization': `${localStorage.getItem('accessToken')}`,
      'Content-Type': 'application/json'
    })
  }

  private buildUrl(source: DATA_SOURCE, path: string) {
    return `${this.apiPath(source)}/${path}`;
  }
}
